import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Check, AlertCircle, Zap } from "lucide-react";

interface LandingHeroProps {
  user: any;
  onScanUrl: (url: string) => void;
}

export function LandingHero({ user, onScanUrl }: LandingHeroProps) {
  const [urlInput, setUrlInput] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) {
      setErrorMsg("ENTER A VALID TARGET URL");
      return;
    }
    setErrorMsg("");
    onScanUrl(urlInput);
  };

  return (
    <section className="min-h-screen w-full flex items-center justify-center bg-white text-black border-b-2 border-black px-6 md:px-20 md:snap-start pt-18 pb-20 relative overflow-hidden">
      {/* Grid background only in Hero as per global rule */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000008_1px,transparent_1px),linear-gradient(to_bottom,#00000008_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
      
      <div className="max-w-6xl w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center z-10 relative">
        
        {/* Left Column: Headline, Pitch & Sandbox Scan Input (7 Cols) */}
        <div className="lg:col-span-7 text-left space-y-8 flex flex-col justify-center">
          
          {/* Tagline pill - 2px solid black, no shadow */}
          <div className="self-start inline-flex items-center gap-1.5 border-2 border-black px-3.5 py-1.5 font-mono text-[10px] font-black uppercase bg-[#FFFF00]">
            <Zap className="h-3.5 w-3.5 fill-black text-black stroke-[3]" /> CONNECT SENTRY, DATADOG & GITHUB IN 90 SECONDS
          </div>

          {/* H1 - Black text only, no gray, all caps */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl xl:text-6xl font-black uppercase tracking-tight leading-[0.95] text-black">
            Your 3am Alert Shouldn’t Ruin Your Night.
          </h1>

          {/* Subheading - Sentence case, brand case 'UptimeWing', punchy and clean copy */}
          <p className="font-sans text-sm sm:text-base md:text-lg font-bold tracking-tight text-zinc-700 max-w-xl leading-relaxed">
            You’re a solo founder. Or a 5-person team. You ship fast. Things break. Right now, when your app goes down, you get 5 alerts from 5 tools. You spend 20 minutes clicking between Sentry, GitHub, and Datadog trying to figure out why. We fixed that.
          </p>

          {/* URL Scanner Console area - Clean and container-less */}
          <div className="w-full max-w-xl space-y-4">
            {user ? (
              <div className="space-y-4 border-4 border-black p-6 bg-yellow-50 shadow-[4px_4px_0_0_#000] font-mono">
                <p className="text-[10px] font-black uppercase text-zinc-500">OPERATIONAL CONSOLE</p>
                <h3 className="text-sm font-black uppercase">
                  Welcome Back, {user.user_metadata?.full_name || user.email || "Operator"}
                </h3>
                <p className="font-sans text-xs text-zinc-700 font-bold">
                  Your telemetry streams are active and auto-recovery rules are active. Proceed to command center dashboard to view services.
                </p>
                <div className="pt-2">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center bg-black text-white border-2 border-black rounded-none px-6 h-12 font-black uppercase hover:bg-black/90 transition-all text-xs shadow-[2px_2px_0_0_#ef4444]"
                  >
                    Go to Dashboard &rarr;
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest font-black">
                  Already have a site? We’ll find your stack.
                </p>
                
                <form onSubmit={handleFormSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => {
                      setUrlInput(e.target.value);
                      if (errorMsg) setErrorMsg("");
                    }}
                    placeholder="yoursaas.com"
                    className="flex-1 h-12 px-4 rounded-none border-2 border-black font-mono text-xs uppercase font-bold focus:ring-0 focus:border-black bg-white"
                  />
                  <button
                    type="submit"
                    className="bg-black text-white border-2 border-black rounded-none px-6 h-12 font-black uppercase hover:bg-yellow-300 hover:text-black transition-all text-xs flex items-center justify-center gap-2 shadow-[2px_2px_0_0_#ef4444]"
                  >
                    <Play className="h-3.5 w-3.5 fill-current" /> Scan My Stack →
                  </button>
                </form>
                <p className="text-[9px] font-mono text-zinc-400 font-black uppercase">
                  Takes 3 seconds. Free for your first app. No credit card.
                </p>
                
                {errorMsg && (
                  <p className="font-mono text-[9px] text-red-500 font-bold uppercase flex items-center gap-1.5">
                    <AlertCircle className="h-3 w-3" /> {errorMsg}
                  </p>
                )}
              </>
            )}
          </div>

        </div>

        {/* Right Column: High-Fidelity Mockup - 2px border, no shadow */}
        <div className="lg:col-span-5 flex items-center justify-center">
          <div className="w-full max-w-md border-2 border-black bg-zinc-950 text-white font-mono text-xs overflow-hidden flex flex-col">
            
            {/* Mockup Header */}
            <div className="p-4 border-b-2 border-black bg-zinc-900 flex justify-between items-center">
              <span className="text-[10px] text-zinc-550 font-black tracking-widest font-bold">UPTIMEWING</span>
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className="w-2 h-2 rounded-full bg-green-500" />
              </div>
            </div>

            {/* Outage status details */}
            <div className="p-5 space-y-4">
              
              <div className="space-y-1">
                <p className="text-zinc-500 text-[10px] font-black uppercase">WHAT BROKE</p>
                <p className="font-black text-white select-all">api-prod DOWN</p>
              </div>

              <div className="border-t border-zinc-900 my-2" />

              <div className="space-y-1">
                <p className="text-zinc-500 text-[10px] font-black uppercase">WHY IT BROKE</p>
                <p className="text-zinc-300 leading-normal">Deploy 241 caused DB timeout 8m ago</p>
                <p className="text-zinc-350">Impact: Checkout -12%</p>
              </div>

              <div className="border-t border-zinc-900 my-2" />

              <div className="space-y-2">
                <p className="text-zinc-500 text-[10px] font-black uppercase">HOW TO FIX</p>
                <div className="bg-[#FFFF00]/10 border border-[#FFFF00]/30 p-3 flex justify-between items-center text-[#FFFF00]">
                  <span className="font-black select-all text-[10px]">vercel rollback --env=production</span>
                  <span className="text-[9px] border border-[#FFFF00] px-1.5 py-0.5 uppercase font-black">COPY</span>
                </div>
              </div>

            </div>

          </div>
        </div>

      </div>
    </section>
  );
}
